import { ConfigurationPageHeader } from '@modules/configuration/components/ConfigurationPageHeader';
import {
  configurationPageClass,
  configurationPageContentClass,
} from '@modules/configuration/components/configurationLayout';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal, ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { PresetsGrid } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { SearchBar } from '../interaction';
import { ApplyPresetModal, ApplyPresetModalRef } from '../modals';
import { usePresetActions } from '../../hooks/usePresetActions';
import { useShippingRulePresets } from '../../hooks/useShippingRulePresets';
import { ShippingRulePreset } from '../../models/ShippingRulePreset';

export const Main = () => {
  const { t } = useTranslation('shippingRulePresetsModule');
  const navigate = useNavigate();

  const { params, updateParam } = useQueryParams({
    search: { type: 'string', default: '' },
  });

  const searchTerm = params.search ?? '';
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const applyModalRef = useRef<ApplyPresetModalRef>(null);
  const confirmModalRef = useRef<ConfirmModalRef>(null);

  const presetsFilters = useMemo(() => ({ search: searchTerm.trim() || undefined }), [searchTerm]);

  const {
    presets,
    isLoading: presetsLoading,
    isFetching: presetsFetching,
    error: presetsError,
    refetch: presetsRefetch,
    isFetchingNextPage: presetsFetchingNext,
  } = useShippingRulePresets(presetsFilters);

  const { duplicatePreset, deletePreset, isLoading: isPresetActionLoading } = usePresetActions(presetsRefetch);

  useEffect(() => {
    if (!presetsLoading && isInitialLoad) setIsInitialLoad(false);
  }, [presetsLoading, isInitialLoad]);

  const handleSearch = useCallback((value: string) => updateParam('search', value), [updateParam]);

  const goToCreatePresetEditor = useCallback(() => {
    navigate('/configuration/shipping-rule-presets/create');
  }, [navigate]);

  const goToHistory = useCallback(() => {
    navigate('/configuration/shipping-rule-presets/history');
  }, [navigate]);

  usePageNavigation({
    breadcrumbs: [{ label: t('title') }],
  });

  const handleApply = useCallback((preset: ShippingRulePreset) => {
    applyModalRef.current?.open(preset);
  }, []);

  const handleEdit = useCallback(
    (preset: ShippingRulePreset) => {
      navigate(`/configuration/shipping-rule-presets/${preset.id}/edit`);
    },
    [navigate],
  );

  const handleDuplicate = useCallback(
    (preset: ShippingRulePreset) => {
      duplicatePreset(preset);
    },
    [duplicatePreset],
  );

  const handleDelete = useCallback(
    (preset: ShippingRulePreset) => {
      confirmModalRef.current?.open(
        () => deletePreset(preset.id),
        undefined,
        t('confirmDelete.title'),
        t('confirmDelete.message'),
      );
    },
    [deletePreset, t],
  );

  const hasActiveSearch = useMemo(() => searchTerm.trim().length > 0, [searchTerm]);

  if (isInitialLoad && presetsLoading) {
    return <LoadingState />;
  }

  if (presetsError) {
    return <ErrorState error={presetsError as Error} onRetry={presetsRefetch} />;
  }

  return (
    <div className={`${configurationPageClass} pb-6`}>
      <ConfigurationPageHeader
        title={t('title')}
        description={t('description')}
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={goToHistory} data-testid="view-history-button">
              {t('history.openButton')}
            </Button>
            <Button onClick={goToCreatePresetEditor} data-testid="create-preset-button">
              {t('buttons.create')}
            </Button>
          </div>
        }
      />

      <div className={configurationPageContentClass}>
        <div className="mb-6">
          <SearchBar value={searchTerm} onChange={handleSearch} />
        </div>

        <div className={presets.length > 0 || hasActiveSearch ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'}>
          <PresetsGrid
            presets={presets}
            isFetching={(presetsFetching && !presetsFetchingNext) || isPresetActionLoading}
            hasActiveSearch={hasActiveSearch}
            onApply={handleApply}
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
          />
        </div>

        {presets.length === 0 && !presetsFetching && (
          <EmptyView
            type={hasActiveSearch ? 'noResults' : 'emptyState'}
            icon={EMPTY_VIEW_ICONS.DEFAULT}
            customAction={
              hasActiveSearch ? null : (
                <Button onClick={goToCreatePresetEditor} data-testid="create-preset-empty-button">
                  {t('buttons.create')}
                </Button>
              )
            }
          />
        )}
      </div>

      <ApplyPresetModal ref={applyModalRef} onSuccess={presetsRefetch} />
      <ConfirmModal ref={confirmModalRef} />
    </div>
  );
};
